import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ChatbotWidget from "../components/ChatbotWidget";
import { getParentOriginFromReferrer } from "../lib/embedOrigins";

const DEFAULT_TITLE = "Chat with Maya";
const DEFAULT_PRIMARY = "#459EA3";

function EmptyEmbedHelp() {
  const baseUrl = typeof window !== "undefined"
    ? `${window.location.origin}${window.location.pathname || ""}#/embed`
    : "https://go.freshroad.com/#/embed";
  const iframeSnippet = `<iframe src="${baseUrl}?scriptId=YOUR_SCRIPT_ID" width="420" height="600" frameborder="0" title="Chat"></iframe>`;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100 p-4" style={{ fontFamily: "system-ui, sans-serif" }}>
      <div className="max-w-lg rounded-lg bg-white p-6 text-left shadow">
        <h1 className="mb-2 text-lg font-semibold text-gray-800">Embed the chat widget</h1>
        <p className="mb-4 text-sm text-gray-600">
          Add this code to your webpage. Replace <code className="rounded bg-gray-100 px-1">YOUR_SCRIPT_ID</code> with your script ID from the dashboard.
        </p>
        <pre className="mb-4 overflow-x-auto rounded-lg bg-gray-900 p-4 text-xs text-gray-100">{iframeSnippet}</pre>
        <p className="text-xs text-gray-500">
          Optional: add <code className="rounded bg-gray-100 px-1">&amp;avatarMode=voice</code> for voice-only (none | image | video | voice).
        </p>
      </div>
    </div>
  );
}

function EmbedStatusCard({ title, message }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#eff4f5] p-4" style={{ fontFamily: "system-ui, sans-serif" }}>
      <div className="max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
        <p className="mt-2 text-sm leading-6 text-gray-600">{message}</p>
      </div>
    </div>
  );
}

export default function EmbedPage() {
  const [searchParams] = useSearchParams();
  const scriptId = searchParams.get("scriptId") || "";
  const avatarMode = (searchParams.get("avatarMode") || "none").toLowerCase();
  const validAvatarMode = ["none", "image", "video", "voice"].includes(avatarMode) ? avatarMode : "none";
  const titleParam = searchParams.get("title");
  const primaryParam = searchParams.get("primaryColor");
  const widgetTitle = titleParam != null && titleParam.trim() !== "" ? titleParam.trim() : DEFAULT_TITLE;
  const primaryColor = /^[0-9A-Fa-f]{6}$/.test((primaryParam || "").replace(/^#/, ""))
    ? `#${(primaryParam || "").replace(/^#/, "")}`
    : DEFAULT_PRIMARY;
  const parentOrigin = useMemo(() => getParentOriginFromReferrer(typeof document !== "undefined" ? document.referrer : ""), []);
  const [embedState, setEmbedState] = useState({
    status: scriptId.trim() ? "loading" : "idle",
    script: null,
    embedToken: null,
    message: "",
  });

  useEffect(() => {
    if (!scriptId.trim()) {
      setEmbedState({ status: "idle", script: null, embedToken: null, message: "" });
      return;
    }

    let active = true;
    const apiBase = process.env.REACT_APP_AGENT_API_URL || "";
    const params = new URLSearchParams();
    if (parentOrigin) {
      params.set("parentOrigin", parentOrigin);
    }

    setEmbedState({ status: "loading", script: null, embedToken: null, message: "" });

    fetch(`${apiBase}/api/script-embed/${encodeURIComponent(scriptId.trim())}${params.toString() ? `?${params.toString()}` : ""}`)
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!active) return;
        if (!response.ok || !data?.success) {
          const message = data?.message || "This embed is not authorized for the current site.";
          setEmbedState({
            status: data?.code === "EMBED_ORIGIN_NOT_ALLOWED" ? "blocked" : "error",
            script: null,
            embedToken: null,
            message,
          });
          return;
        }
        setEmbedState({
          status: "ready",
          script: data.script || null,
          embedToken: data.embedToken || null,
          message: "",
        });
      })
      .catch((error) => {
        if (!active) return;
        setEmbedState({
          status: "error",
          script: null,
          embedToken: null,
          message: error?.message || "Failed to load the embedded chatbot.",
        });
      });

    return () => {
      active = false;
    };
  }, [parentOrigin, scriptId]);

  if (!scriptId.trim()) {
    return <EmptyEmbedHelp />;
  }

  if (embedState.status === "loading") {
    return <EmbedStatusCard title="Loading chatbot" message="Checking embed access and preparing the widget." />;
  }

  if (embedState.status === "blocked") {
    return <EmbedStatusCard title="Embed blocked" message={embedState.message || "This chatbot is not allowed to run on this site."} />;
  }

  if (embedState.status === "error") {
    return <EmbedStatusCard title="Unable to load chatbot" message={embedState.message || "The embed could not be initialized."} />;
  }

  return (
    <div className="flex min-h-screen items-end justify-end bg-transparent p-0">
      <ChatbotWidget
        scriptId={scriptId.trim()}
        avatarMode={validAvatarMode}
        embedMode
        onClose={null}
        widgetTitle={widgetTitle}
        primaryColor={primaryColor}
        initialScriptData={embedState.script}
        embedToken={embedState.embedToken}
        embedParentOrigin={parentOrigin}
      />
    </div>
  );
}
